import { DateTime } from "luxon";

import type {
  Invoice,
  InvoiceExtractedDetail,
  InvoiceExtractedField,
  InvoiceFieldLocation,
  InvoiceRecipientCandidate,
  InvoiceRecipientMatch,
  InvoiceRecipientMatchSummaryInput,
  InvoiceStatus,
} from "./types";

// Statuses at which the async render/extract/match pipeline has finished (either
// successfully or terminally failed) and there is no point polling further.
const TERMINAL_STATUSES = new Set<InvoiceStatus>([
  "ready",
  "extraction_failed",
]);

export type InvoiceRecipientMatchDisplayState =
  | "savedRecipient"
  | "matchedBankDetails"
  | "needsReview"
  | "newRecipient"
  | "recipientUnavailable";

export type InvoiceRecipientBadgeVariant = "success" | "warning";

export type InvoiceRecipientMatchSummary = {
  state: InvoiceRecipientMatchDisplayState;
  title: string | null;
  topCandidates: InvoiceRecipientCandidate[];
  primaryCandidate: InvoiceRecipientCandidate | null;
  showVerifiedBankDetails: boolean;
};

export type InvoiceRecipientBadgeDescriptor = {
  l10nId:
    | "invoice-pay-review-recipient-badge-saved"
    | "invoice-pay-review-recipient-badge-matched"
    | "invoice-pay-review-recipient-badge-new"
    | "invoice-pay-review-recipient-badge-details-missing";
  fallbackLabel: "Saved" | "Matched" | "New recipient" | "Details missing";
  variant: InvoiceRecipientBadgeVariant;
};

type InvoicePayFlowInvoiceLike = Invoice | Pick<Invoice, "status"> | null;

export const getInvoiceRecipientBadgeDescriptor = (
  state: InvoiceRecipientMatchDisplayState,
): InvoiceRecipientBadgeDescriptor | null => {
  switch (state) {
    case "savedRecipient":
      return {
        l10nId: "invoice-pay-review-recipient-badge-saved",
        fallbackLabel: "Saved",
        variant: "success",
      };
    // "Matched" is reserved for a verified bank-details match
    case "matchedBankDetails":
      return {
        l10nId: "invoice-pay-review-recipient-badge-matched",
        fallbackLabel: "Matched",
        variant: "success",
      };
    case "needsReview":
      return {
        l10nId: "invoice-pay-review-recipient-badge-details-missing",
        fallbackLabel: "Details missing",
        variant: "warning",
      };
    case "newRecipient":
      return {
        l10nId: "invoice-pay-review-recipient-badge-new",
        fallbackLabel: "New recipient",
        variant: "warning",
      };
    case "recipientUnavailable":
      return {
        l10nId: "invoice-pay-review-recipient-badge-details-missing",
        fallbackLabel: "Details missing",
        variant: "warning",
      };
    default:
      return null;
  }
};

export const isInvoiceReady = (invoice: InvoicePayFlowInvoiceLike): boolean =>
  invoice?.status === "ready";

export const canContinueInvoicePayFlow = (
  invoice: InvoicePayFlowInvoiceLike,
): boolean => Boolean(invoice && TERMINAL_STATUSES.has(invoice.status));

export const requiresInvoicePayManualEntry = (
  invoice: InvoicePayFlowInvoiceLike,
): boolean => invoice?.status === "extraction_failed";

export const isInvoiceTerminal = (invoice: Invoice | null): boolean =>
  !!invoice && TERMINAL_STATUSES.has(invoice.status);

export const isInvoiceRenderable = (invoice: Invoice | null): boolean =>
  !!invoice && invoice.pages.length > 0;

// The full backend pipeline. There are more stages here than the scanning-checklist
// rows shown to the user, so progress below is spread evenly across it.
const STATUS_ORDER: InvoiceStatus[] = [
  "uploaded",
  "rendering",
  "analyzing",
  "arbitrating",
  "matching_recipient",
  "ready",
];

// statuses at which each scanning-checklist row should flip to "done", in the same
// order as the rows shown on the scanning screen (recipient/bank details, amount/due
// date, payment method)
// only `analyzing` and `arbitrating` last long enough for a poll to observe them;
// `rendering` and `matching_recipient` are shorter than the poll interval and are effectively never seen
// the three checkmark rows are therefore pinned to those two real boundaries plus the terminal state
const ROW_COMPLETE_AT_STATUS: InvoiceStatus[] = [
  "analyzing",
  "arbitrating",
  "ready",
];

export const INVOICE_SCANNING_ROW_COUNT = ROW_COMPLETE_AT_STATUS.length;

// how long the *final* checklist row may stay in progress before the UI reassures the
// user that it's still working. measured against that row rather than the whole run
export const INVOICE_STILL_PROCESSING_DELAY_MS = 15000;

// Gap between scanning-checklist rows flipping to "done". The pipeline doesn't
// pace itself: `matching_recipient` regularly lasts tens of milliseconds, so a
// poll almost never observes it and the row count jumps straight from 1 to 3.
// Revealing earned rows one at a time keeps that readable — the count is still
// clamped to what the backend actually reported, so this never runs ahead.
export const INVOICE_SCANNING_ROW_STAGGER_MS = 400;

// How many scanning-checklist rows should show as complete for a given invoice status,
// so web and mobile render the same top-to-bottom progression.
export const getInvoiceScanningCompletedRowCount = (
  status: InvoiceStatus | null,
): number => {
  if (status && TERMINAL_STATUSES.has(status)) {
    return INVOICE_SCANNING_ROW_COUNT;
  }

  const statusIndex = status ? STATUS_ORDER.indexOf(status) : -1;
  if (statusIndex === -1) {
    return 0;
  }
  return ROW_COMPLETE_AT_STATUS.filter(
    (checkpoint) => statusIndex >= STATUS_ORDER.indexOf(checkpoint),
  ).length;
};

const sortInvoiceRecipientMatchCandidates = (
  candidates: InvoiceRecipientCandidate[],
): InvoiceRecipientCandidate[] =>
  [...candidates].sort((left, right) => left.rank - right.rank);

const getTopRankedInvoiceRecipientMatchCandidates = (
  candidates: InvoiceRecipientCandidate[],
): InvoiceRecipientCandidate[] => {
  const sortedCandidates = sortInvoiceRecipientMatchCandidates(candidates);
  const bestRank = sortedCandidates[0]?.rank;

  return bestRank == null
    ? []
    : sortedCandidates.filter((candidate) => candidate.rank === bestRank);
};

const getPrimaryInvoiceRecipientMatchCandidate = (
  candidates: InvoiceRecipientCandidate[],
): InvoiceRecipientCandidate | null => {
  const topCandidates = getTopRankedInvoiceRecipientMatchCandidates(candidates);

  return topCandidates.length === 1 ? topCandidates[0] : null;
};

export const toInvoiceRecipientMatchSummaryInput = (
  invoice?: {
    status?: InvoiceStatus | null;
    recipient_match?: InvoiceRecipientMatch | null;
    extracted?: Partial<InvoiceExtractedDetail> | null;
  } | null,
): InvoiceRecipientMatchSummaryInput => ({
  invoiceStatus: invoice?.status ?? null,
  recipientMatch: invoice?.recipient_match ?? null,
  extracted: invoice?.extracted ?? null,
});

const hasCompleteExtractedRecipientDetails = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): boolean =>
  Boolean(
    extracted?.recipient_name?.value &&
      extracted?.remittance_account?.value &&
      extracted?.remittance_routing?.value,
  );

export type InvoiceDetailsFormDefaults = {
  amount: string;
  date: string;
  memo: string;
};

const getExtractedDateField = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): InvoiceExtractedField | null =>
  (extracted?.due_date?.value ? extracted.due_date : extracted?.invoice_date) ??
  null;

const formatExtractedInvoiceDate = (isoDate?: string | null): string => {
  const parsedDate = isoDate ? DateTime.fromISO(isoDate) : null;

  return parsedDate?.isValid ? parsedDate.toFormat("MM/dd/yyyy") : "";
};

export const getInvoiceDueDateFormValue = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): string => formatExtractedInvoiceDate(extracted?.due_date?.value);

// prefill values for the invoice-pay details form, sourced from the invoice's
// own extracted fields rather than left for the user to type from scratch
export const getInvoiceDetailsFormDefaults = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): InvoiceDetailsFormDefaults => {
  const invoiceNumber = extracted?.invoice_number?.value;

  return {
    amount: extracted?.amount?.value ?? "",
    date: formatExtractedInvoiceDate(getExtractedDateField(extracted)?.value),
    memo: invoiceNumber ? `#${invoiceNumber}` : "",
  };
};

export type InvoiceDetailsFieldLocations = {
  amount: InvoiceFieldLocation | null;
  date: InvoiceFieldLocation | null;
  memo: InvoiceFieldLocation | null;
};

// maps each invoice-pay details form field to the source field it was extracted from,
// so the UI can scroll/highlight the invoice preview to the right spot on focus
export const getInvoiceFieldLocations = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): InvoiceDetailsFieldLocations => ({
  amount: extracted?.amount?.location ?? null,
  date: getExtractedDateField(extracted)?.location ?? null,
  memo: extracted?.invoice_number?.location ?? null,
});

export type InvoiceMissingFields = {
  amount: boolean;
  date: boolean;
  memo: boolean;
};

// which invoice-pay details form fields couldn't be extracted at all, so the UI can
// prompt the member to fill them in rather than silently leaving them blank.
// memo is never flagged - it's an optional field, unlike amount/date
export const getInvoiceMissingFields = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): InvoiceMissingFields => ({
  amount: !extracted?.amount,
  date: !getExtractedDateField(extracted),
  memo: false,
});

export const isInvoiceForeignCurrency = (
  extracted?: Partial<InvoiceExtractedDetail> | null,
): boolean => {
  const currency = extracted?.currency?.trim();

  return !!currency && currency.toUpperCase() !== "USD";
};

export const getInvoiceRecipientMatchSummary = (
  input?: InvoiceRecipientMatchSummaryInput | null,
): InvoiceRecipientMatchSummary => {
  const recipientMatch = input?.recipientMatch;

  if (!recipientMatch || recipientMatch.status == null) {
    return {
      state: "recipientUnavailable",
      title: input?.extracted?.recipient_name?.value ?? null,
      topCandidates: [],
      primaryCandidate: null,
      showVerifiedBankDetails: false,
    };
  }

  const topCandidates = getTopRankedInvoiceRecipientMatchCandidates(
    recipientMatch.candidates,
  );
  // only trust a candidate as *the* match for statuses that are actually confident in
  // one - an "ambiguous" payload can still carry a single candidate entry, but the
  // status itself is telling us not to treat it as resolved
  const isConfidentStatus =
    recipientMatch.status === "matched_bank_details" ||
    recipientMatch.status === "strong_candidate";
  const confidentCandidate = isConfidentStatus
    ? getPrimaryInvoiceRecipientMatchCandidate(recipientMatch.candidates)
    : null;
  const hasBankDetailsMatch = recipientMatch.status === "matched_bank_details";
  const hasBankDetailsMismatch =
    recipientMatch.status === "strong_candidate" &&
    confidentCandidate?.match_type === "name_fuzzy_bank_details_mismatch" &&
    hasCompleteExtractedRecipientDetails(input?.extracted);
  const primaryCandidate = hasBankDetailsMismatch ? null : confidentCandidate;
  let state: InvoiceRecipientMatchDisplayState = "recipientUnavailable";

  if (hasBankDetailsMatch) {
    state = "matchedBankDetails";
  } else if (hasBankDetailsMismatch) {
    state = "newRecipient";
  } else if (
    recipientMatch.status === "no_match" ||
    recipientMatch.status === "ambiguous"
  ) {
    state = hasCompleteExtractedRecipientDetails(input?.extracted)
      ? "newRecipient"
      : "recipientUnavailable";
  } else if (recipientMatch.status === "strong_candidate") {
    state = "needsReview";
  }

  return {
    state,
    title:
      primaryCandidate?.recipient_name ??
      input?.extracted?.recipient_name?.value ??
      null,
    topCandidates,
    primaryCandidate,
    showVerifiedBankDetails: hasBankDetailsMatch,
  };
};
